import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AuthActions, LoggerService, ProductActions, ProductConnector, ScopedProductData, SiteContextActions, tryNormalizeHttpError, withdrawOn } from '@spartacus/core';
import { merge, Observable, of, OperatorFunction, SchedulerLike } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, tap } from 'rxjs/operators';

@Injectable()
export class CustomProductEffects {
  protected logger = inject(LoggerService);
  private contextChange$: Observable<Action> = this.actions$.pipe(
    ofType(SiteContextActions.CURRENCY_CHANGE, SiteContextActions.LANGUAGE_CHANGE, AuthActions.LOGIN, AuthActions.LOGOUT)
  );

  loadProduct$ = createEffect(
    () =>
      ({ scheduler, debounce = 0 } = {}): Observable<ProductActions.LoadProductSuccess | ProductActions.LoadProductFail> =>
        this.actions$.pipe(
          ofType(ProductActions.LOAD_PRODUCT),
          map((action: ProductActions.LoadProduct) => ({
            code: action.payload,
            scope: action.meta.scope
          })),
          bufferDebounceTime(debounce, scheduler),
          mergeMap((products) => merge(...this.productConnector.getMany(products).map(this.productLoadEffect))),
          withdrawOn(this.contextChange$)
        )
  );

  private productLoadEffect(productLoad: ScopedProductData): Observable<ProductActions.LoadProductSuccess | ProductActions.LoadProductFail> {
    return productLoad.data$.pipe(
      map((data) => new ProductActions.LoadProductSuccess({ code: productLoad.code, ...data }, productLoad.scope)),
      catchError((error) => {
        return of(new ProductActions.LoadProductFail(productLoad.code, tryNormalizeHttpError(error, this.logger), productLoad.scope));
      })
    );
  }

  constructor(private actions$: Actions, private productConnector: ProductConnector) {}
}

export function bufferDebounceTime<T>(time: number = 0, scheduler?: SchedulerLike): OperatorFunction<T, T[]> {
  return (source: Observable<T>) => {
    let bufferedValues: T[] = [];

    return source.pipe(
      tap((value) => bufferedValues.push(value)),
      debounceTime(time, scheduler),
      map(() => bufferedValues),
      tap(() => (bufferedValues = []))
    );
  };
}
